import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { getAgeFromDob, getCategoryNameForDob } from '../../utils/playerCategory'

const AddPlayer = () => {
  const navigate = useNavigate()
  const [states, setStates] = useState([])
  const [districts, setDistricts] = useState([])
  const [masterCategories, setMasterCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [userStateName, setUserStateName] = useState('')
  const [formData, setFormData] = useState({
    member_type: 'player',
    full_name: '',
    father_name: '',
    date_of_birth: '',
    gender: 'M',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    pin_code: '',
    district: '',
    state_id: '',
    position: 'Player',
    aadhar_card: '',
    passport_number: '',
    primary_contact: '',
    secondary_contact: '',
    email: '',
    marital_status: '',
    category: '' // Will be auto-calculated from DOB
  })

  const positions = {
    player: ['Player'],
    coach: ['Head Coach', 'Assistant Coach'],
    manager: ['Team Manager', 'Operations Manager', 'Equipment Manager', 'Travel Coordinator']
  }

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

  useEffect(() => {
    fetchUserProfile()
    fetchStates()
    fetchMasterCategories()
  }, [])

  // Category always follows the current DOB. Recomputed (and cleared when no
  // category covers the age) on every change, including once the category
  // master finishes loading, so a value from an earlier DOB can't linger.
  useEffect(() => {
    const category = formData.member_type === 'player'
      ? getCategoryNameForDob(formData.date_of_birth, masterCategories)
      : ''
    setFormData(prev => (prev.category === category ? prev : { ...prev, category }))
  }, [formData.date_of_birth, formData.member_type, masterCategories])

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/user/profile')
      if (response.data.success) {
        const userData = response.data.data
        // Auto-select user's state and lock it
        if (userData.state_id) {
          setFormData(prev => ({ ...prev, state_id: userData.state_id }))
          setUserStateName(userData.state || '')
          // Fetch districts for user's state
          fetchDistricts(userData.state_id)
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }

  const fetchDistricts = async (stateId) => {
    if (!stateId) return
    try {
      const response = await api.get(`/user/states/${stateId}/districts`)
      if (response.data.success) {
        setDistricts(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching districts:', error)
    }
  }

  const fetchStates = async () => {
    try {
      const response = await api.get('/user/states')
      if (response.data.success) {
        setStates(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching states:', error)
    }
  }

  const fetchMasterCategories = async () => {
    try {
      const response = await api.get('/user/categories')
      if (response.data.success || response.data.data) {
        setMasterCategories(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // Fetch districts when state changes
    if (name === 'state_id' && value) {
      fetchDistricts(value)
      // Reset district when state changes
      setFormData(prev => ({ ...prev, [name]: value, district: '' }))
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size should be less than 2MB' }))
        return
      }

      setErrors(prev => ({ ...prev, image: '' }))
      setImageFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!imageFile) newErrors.image = 'Photo is required'
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required'
    if (!formData.father_name.trim()) newErrors.father_name = "Father's name is required"
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required'
    if (!formData.state_id) newErrors.state_id = 'State is required'
    if (!formData.gender) newErrors.gender = 'Gender is required'

    // Validate email format if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    // Validate phone number format if provided
    if (formData.primary_contact && !/^\d{10}$/.test(formData.primary_contact)) {
      newErrors.primary_contact = 'Contact must be 10 digits'
    }
    if (formData.secondary_contact && !/^\d{10}$/.test(formData.secondary_contact)) {
      newErrors.secondary_contact = 'Contact must be 10 digits'
    }

    // Validate Aadhar format if provided
    if (formData.aadhar_card && !/^\d{12}$/.test(formData.aadhar_card)) {
      newErrors.aadhar_card = 'Aadhar must be 12 digits'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const currentAge = getAgeFromDob(formData.date_of_birth)
  const categoryDisplay = formData.category ||
    (masterCategories.length ? 'No category for this age' : 'Loading categories...')

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      alert('Please fill all required fields correctly')
      return
    }

    // Show confirmation modal instead of directly submitting
    setShowConfirmModal(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false)
    setLoading(true)

    try {
      const formDataToSend = new FormData()

      // Append all form fields (category is already auto-calculated in formData)
      Object.keys(formData).forEach(key => {
        // Skip empty values except for category (it might be calculated)
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key])
        }
      })

      // Debug: Log category value
      console.log('Category being sent:', formData.category)

      // Append image if selected
      if (imageFile) {
        formDataToSend.append('player_image', imageFile)
      }

      const response = await api.post('/user/my-players', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        alert('Member created successfully!')
        navigate('/user/my-players')
      }
    } catch (error) {
      console.error('Error creating member:', error)
      alert(error.response?.data?.message || 'Failed to create member')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="add-player-page">
      {/* Page Header with gradient background */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-user-plus"></i>
          </div>
          <div className="header-text">
            <h1>Add New Member</h1>
            <p>Register a new member for approval</p>
          </div>
        </div>
        <button className="btn-back" style={{ marginLeft: 'auto' }} onClick={() => navigate('/user/my-players')}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Image Upload Section */}
          <div className="image-upload-section">
            <div className="image-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Player" />
              ) : (
                <div className="placeholder">
                  <i className="fas fa-user-circle"></i>
                  <p>Upload Photo</p>
                </div>
              )}
            </div>
            <div className="image-upload-controls">
              <label className="btn-upload">
                <i className="fas fa-camera"></i> Choose Photo <span style={{ color: '#ffd1d1', marginLeft: '2px' }}>*</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              <small>JPG, PNG, GIF (Max 2MB)</small>
              {errors.image && (
                <div className="image-error-text">
                  <i className="fas fa-exclamation-circle"></i> {errors.image}
                </div>
              )}
            </div>
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h3><i className="fas fa-user"></i> Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Member Type <span className="required">*</span></label>
                <select
                  name="member_type"
                  value={formData.member_type}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      member_type: e.target.value,
                      position: positions[e.target.value][0] // Reset position to first option
                    }))
                  }}
                  className={errors.member_type ? 'error' : ''}
                  required
                >
                  <option value="player">Player</option>
                  <option value="coach">Coach</option>
                  <option value="manager">Manager</option>
                </select>
                {errors.member_type && <span className="error-text">{errors.member_type}</span>}
              </div>

              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder={`Enter ${formData.member_type}'s full name`}
                  className={errors.full_name ? 'error' : ''}
                  required
                />
                {errors.full_name && <span className="error-text">{errors.full_name}</span>}
              </div>

              <div className="form-group">
                <label>Father's Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="father_name"
                  value={formData.father_name}
                  onChange={handleInputChange}
                  placeholder="Enter father's name"
                  className={errors.father_name ? 'error' : ''}
                  required
                />
                {errors.father_name && <span className="error-text">{errors.father_name}</span>}
              </div>

              <div className="form-group">
                <label>Date of Birth <span className="required">*</span></label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]}
                  className={errors.date_of_birth ? 'error' : ''}
                  required
                />
                {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
                {currentAge && formData.member_type === 'player' && (
                  <small className="info-text">
                    Age: {currentAge} years - Auto Category: {categoryDisplay}
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Gender <span className="required">*</span></label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={errors.gender ? 'error' : ''}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
                {errors.gender && <span className="error-text">{errors.gender}</span>}
              </div>

              <div className="form-group">
                <label>Blood Group</label>
                <select
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleInputChange}
                >
                  <option value="">Select Blood Group</option>
                  {bloodGroups.map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Height (cm)</label>
                <input
                  type="number"
                  name="height"
                  value={formData.height}
                  onChange={handleInputChange}
                  placeholder="Height in cm"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Weight (kg)</label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="Weight in kg"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Marital Status</label>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleInputChange}
                >
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <h3><i className="fas fa-phone"></i> Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Contact Number - Primary</label>
                <input
                  type="tel"
                  name="primary_contact"
                  value={formData.primary_contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    setFormData(prev => ({ ...prev, primary_contact: value }))
                    if (errors.primary_contact) setErrors(prev => ({ ...prev, primary_contact: '' }))
                  }}
                  placeholder="10-digit primary contact number"
                  maxLength="10"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={errors.primary_contact ? 'error' : ''}
                />
                {errors.primary_contact && <span className="error-text">{errors.primary_contact}</span>}
              </div>

              <div className="form-group">
                <label>Contact Number - Secondary</label>
                <input
                  type="tel"
                  name="secondary_contact"
                  value={formData.secondary_contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    setFormData(prev => ({ ...prev, secondary_contact: value }))
                    if (errors.secondary_contact) setErrors(prev => ({ ...prev, secondary_contact: '' }))
                  }}
                  placeholder="10-digit secondary contact number"
                  maxLength="10"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={errors.secondary_contact ? 'error' : ''}
                />
                {errors.secondary_contact && <span className="error-text">{errors.secondary_contact}</span>}
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="member@example.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="form-section">
            <h3><i className="fas fa-map-marker-alt"></i> Address Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter complete address"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>State <span className="required">*</span></label>
                <input
                  type="text"
                  value={userStateName || 'Auto-filled from profile'}
                  disabled
                  className="auto-field"
                />
              </div>

              <div className="form-group">
                <label>District</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  disabled={!formData.state_id || districts.length === 0}
                >
                  <option value="">{formData.state_id ? 'Select District' : 'Select State First'}</option>
                  {districts.map(district => (
                    <option key={district.id} value={district.name}>{district.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>PIN Code</label>
                <input
                  type="text"
                  name="pin_code"
                  value={formData.pin_code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setFormData(prev => ({ ...prev, pin_code: value }))
                  }}
                  placeholder="6-digit PIN code"
                  maxLength="6"
                  pattern="[0-9]*"
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>

          {/* Playing Information */}
          <div className="form-section">
            <h3><i className="fas fa-baseball-ball"></i> Playing Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  {formData.member_type === 'player' && 'Playing Position'}
                  {formData.member_type === 'coach' && 'Coaching Role'}
                  {formData.member_type === 'manager' && 'Management Role'}
                </label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                >
                  <option value="">Select {formData.member_type === 'player' ? 'Position' : 'Role'}</option>
                  {positions[formData.member_type]?.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>

              {currentAge && formData.member_type === 'player' && (
                <div className="form-group">
                  <label>Category (Auto-calculated from DOB)</label>
                  <input
                    type="text"
                    value={categoryDisplay}
                    disabled
                    className="auto-field"
                  />
                  <small className="info-text">
                    Category is automatically set based on date of birth
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* Identity Information */}
          <div className="form-section">
            <h3><i className="fas fa-id-card"></i> Identity Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Aadhar Card Number</label>
                <input
                  type="text"
                  name="aadhar_card"
                  value={formData.aadhar_card}
                  onChange={handleInputChange}
                  placeholder="12-digit Aadhar number"
                  maxLength="12"
                  className={errors.aadhar_card ? 'error' : ''}
                />
                {errors.aadhar_card && <span className="error-text">{errors.aadhar_card}</span>}
              </div>

              <div className="form-group">
                <label>Passport Number</label>
                <input
                  type="text"
                  name="passport_number"
                  value={formData.passport_number}
                  onChange={handleInputChange}
                  placeholder="Passport number (if available)"
                  maxLength="20"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/user/my-players')}
              disabled={loading}
            >
              <i className="fas fa-times"></i> Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Adding...
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i> Add Member
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <i className="fas fa-exclamation-triangle warning-icon"></i>
              <h2>Confirm Member Details</h2>
            </div>
            <div className="modal-body">
              <div className="warning-message">
                <i className="fas fa-info-circle"></i>
                <p><strong>Important:</strong> Once created, you cannot edit these details directly. Any changes will require admin approval.</p>
              </div>
              <div className="confirm-details">
                <h4>Please verify the following information is correct:</h4>
                <div className="detail-row">
                  <span className="label">Member Type:</span>
                  <span className="value">{formData.member_type.charAt(0).toUpperCase() + formData.member_type.slice(1)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Full Name:</span>
                  <span className="value">{formData.full_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Father's Name:</span>
                  <span className="value">{formData.father_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Date of Birth:</span>
                  <span className="value">{formData.date_of_birth}</span>
                </div>
                {formData.gender && (
                  <div className="detail-row">
                    <span className="label">Gender:</span>
                    <span className="value">{formData.gender === 'M' ? 'Male' : 'Female'}</span>
                  </div>
                )}
                {formData.category && (
                  <div className="detail-row">
                    <span className="label">Category:</span>
                    <span className="value">{formData.category}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setShowConfirmModal(false)}>
                <i className="fas fa-times"></i> Go Back & Edit
              </button>
              <button className="btn-modal-confirm" onClick={handleConfirmSubmit}>
                <i className="fas fa-check"></i> Confirm & Create
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .add-player-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: #f3f4f6;
        }

        .page-header-banner {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          border-radius: 12px;
          margin: 1.5rem;
          margin-bottom: 0;
        }

        .btn-back {
          height: 40px;
          padding: 0 18px;
          border-radius: 8px;
          border: none;
          background: #c00f15;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
          white-space: nowrap;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .btn-back:hover {
          background: #a00d12;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .header-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .header-text h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .header-text p {
          color: rgba(255,255,255,0.7);
          margin: 0;
          font-size: 0.875rem;
        }

        .form-container {
          background: white;
          margin: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .image-upload-section {
          display: flex;
          gap: 2rem;
          padding: 2rem;
          background: #f9fafb;
          align-items: center;
          border-bottom: 1px solid #e5e7eb;
        }

        .image-preview {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          overflow: hidden;
          border: 4px solid var(--primary-color, #022a5e);
          background: white;
          flex-shrink: 0;
        }

        .image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          color: #9ca3af;
        }

        .placeholder i {
          font-size: 3rem;
          margin-bottom: 0.5rem;
          color: var(--primary-color, #022a5e);
          opacity: 0.5;
        }

        .placeholder p {
          font-size: 0.875rem;
        }

        .image-upload-controls {
          flex: 1;
        }

        .btn-upload {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: var(--primary-color, #022a5e);
          color: white;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .btn-upload:hover {
          background: var(--primary-hover, #053e88);
        }

        .image-upload-controls small {
          display: block;
          color: #6b7280;
          margin-top: 0.5rem;
        }

        .image-error-text {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 0.75rem;
          padding: 0.5rem 0.75rem;
          color: #b91c1c;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .form-section {
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section:last-of-type {
          border-bottom: none;
        }

        .form-section h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 1.25rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section h3 i {
          color: var(--primary-color, #022a5e);
          font-size: 0.9rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: span 2;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .required {
          color: #ef4444;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.2s;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #9ca3af;
        }

        .form-group input.error,
        .form-group select.error {
          border-color: #ef4444;
        }

        .form-group input.auto-field {
          background: #f3f4f6;
          cursor: not-allowed;
          color: var(--primary-color, #022a5e);
          font-weight: 500;
        }

        .error-text {
          color: #ef4444;
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }

        .info-text {
          color: #c00f15;
          font-size: 0.8rem;
          margin-top: 0.25rem;
          font-weight: 500;
        }

        .form-actions {
          padding: 1.5rem 2rem;
          background: #f9fafb;
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel {
          padding: 0.75rem 1.5rem;
          background: white;
          color: #6b7280;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-submit {
          padding: 0.75rem 1.5rem;
          background: var(--primary-color, #022a5e);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-submit:hover:not(:disabled) {
          background: var(--primary-hover, #053e88);
        }

        .btn-submit:disabled,
        .btn-cancel:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-container {
            margin: 1rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-group.full-width {
            grid-column: span 1;
          }

          .form-section {
            padding: 1.25rem 1.5rem;
          }

          .image-upload-section {
            flex-direction: column;
            text-align: center;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .btn-submit,
          .btn-cancel {
            width: 100%;
            justify-content: center;
          }
        }

        /* Confirmation Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
          animation: fadeIn 0.2s ease-in;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          padding: 2rem 2rem 1.5rem;
          border-bottom: 2px solid #f3f4f6;
          text-align: center;
        }

        .modal-header .warning-icon {
          font-size: 3rem;
          color: #fbbf24;
          margin-bottom: 1rem;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .modal-header h2 {
          margin: 0;
          color: #111827;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .modal-body {
          padding: 2rem;
        }

        .warning-message {
          background: #fef3c7;
          border-left: 4px solid #fbbf24;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          gap: 0.75rem;
          align-items: start;
        }

        .warning-message i {
          color: #d97706;
          font-size: 1.25rem;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .warning-message p {
          margin: 0;
          color: #92400e;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .confirm-details h4 {
          color: #374151;
          font-size: 1rem;
          margin: 0 0 1rem 0;
          font-weight: 600;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 0.5rem;
        }

        .detail-row:nth-child(even) {
          background: #f3f4f6;
        }

        .detail-row .label {
          font-weight: 500;
          color: #6b7280;
          font-size: 0.9rem;
        }

        .detail-row .value {
          font-weight: 600;
          color: #111827;
          font-size: 0.9rem;
          text-align: right;
        }

        .modal-actions {
          padding: 1.5rem 2rem 2rem;
          display: flex;
          gap: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-modal-cancel,
        .btn-modal-confirm {
          flex: 1;
          padding: 0.875rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          border: none;
        }

        .btn-modal-cancel {
          background: #f3f4f6;
          color: #6b7280;
        }

        .btn-modal-cancel:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-modal-confirm {
          background: var(--primary-color, #022a5e);
          color: white;
        }

        .btn-modal-confirm:hover {
          background: var(--primary-hover, #053e88);
        }

        @media (max-width: 640px) {
          .modal-actions {
            flex-direction: column-reverse;
          }

          .btn-modal-cancel,
          .btn-modal-confirm {
            width: 100%;
          }

          .modal-body {
            padding: 1.5rem;
          }

          .detail-row {
            flex-direction: column;
            gap: 0.25rem;
          }

          .detail-row .value {
            text-align: left;
          }
        }
      `}</style>
    </div>
  )
}

export default AddPlayer
